import { useEffect, useRef } from "react";

import { canClaimGlobalKey } from "./guards";
import { onPressInterrupt } from "./interrupts";

/**
 * Tap-vs-hold discrimination for a single global key: a tap submits, a hold
 * opens push-to-talk. A press is ambiguous while it happens, so the decision
 * waits for a key-up (tap) or a timer (hold) — and a press can also end in
 * ways that are neither (blur, tab switch, unmount).
 *
 * `onHoldStart` may be async (the tutor awaits a canvas PNG export), so a
 * key-up can land while the start is still resolving. A DOM listener cannot
 * wait, so a release arriving in that window is recorded and applied once the
 * start settles: exactly one end-or-cancel per press, never zero (mic stuck
 * on) and never two. A new press is refused while a start is in flight.
 */
export type HoldKeyOptions = {
  /** `KeyboardEvent.code`, e.g. `"KeyS"`. */
  code: string;
  /** Press shorter than this is a tap; longer is a hold. */
  thresholdMs: number;
  /** When false the key is not listened for, and any press in flight is ended. */
  enabled: boolean;
  /** Override the "user is typing" test. Defaults to the shared DOM guard. */
  isTyping?: (target: EventTarget | null) => boolean;
  /**
   * Key down. Anything reversible may start here (both callers begin buffering
   * mic audio so the threshold costs no audio). Return `false` if nothing was
   * armed, in which case `onAbortArm` is not called.
   */
  onArm?: () => boolean | void;
  /** The press ended without ever becoming a hold — discard whatever `onArm` began. */
  onAbortArm?: () => void;
  /** The arm was carried into a live hold; it is no longer pending. */
  onArmCommitted?: () => void;
  /**
   * The threshold fired: this is a hold. May be async. Resolve `false` if the
   * hold did not take (the caller has already cleaned up); `onHoldEnd` is then
   * not called for this press.
   */
  onHoldStart: () => void | boolean | Promise<void | boolean>;
  /** The hold ended — by key-up, blur, tab switch, disable or unmount. */
  onHoldEnd: () => void;
  /** The press was shorter than the threshold. */
  onTap?: () => void;
};

type PressState = {
  keyOwned: boolean;
  holdStarted: boolean;
  armed: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  generation: number;
  /** Generation whose `onHoldStart` is still resolving, else null. */
  startInFlight: number | null;
  /** A release arrived mid-start and must be applied when it settles. */
  releaseDeferred: boolean;
};

export function useHoldKey({
  code,
  thresholdMs,
  enabled,
  isTyping,
  onArm,
  onAbortArm,
  onArmCommitted,
  onHoldStart,
  onHoldEnd,
  onTap,
}: HoldKeyOptions): void {
  const state = useRef<PressState>({
    keyOwned: false,
    holdStarted: false,
    armed: false,
    timer: null,
    generation: 0,
    startInFlight: null,
    releaseDeferred: false,
  });

  // Assigned during render, not in an effect, so handlers can never read a
  // stale callback between render and commit.
  const cb = useRef({
    isTyping,
    onArm,
    onAbortArm,
    onArmCommitted,
    onHoldStart,
    onHoldEnd,
    onTap,
  });
  cb.current = {
    isTyping,
    onArm,
    onAbortArm,
    onArmCommitted,
    onHoldStart,
    onHoldEnd,
    onTap,
  };

  const clearTimer = () => {
    const s = state.current;
    if (s.timer) {
      clearTimeout(s.timer);
      s.timer = null;
    }
  };

  /** Discard whatever `onArm` started, if anything is still pending. */
  const discardArm = () => {
    const s = state.current;
    if (!s.armed) return;
    s.armed = false;
    cb.current.onAbortArm?.();
  };

  /**
   * End a press that can no longer receive its key-up. Does exactly one of:
   * defer, end the hold, or drop the arm.
   */
  const abandon = () => {
    const s = state.current;
    clearTimer();
    if (!s.keyOwned && s.startInFlight === null) return;
    s.keyOwned = false;
    if (s.startInFlight !== null) {
      s.releaseDeferred = true;
      return;
    }
    if (s.holdStarted) {
      s.holdStarted = false;
      cb.current.onHoldEnd();
      return;
    }
    discardArm();
  };

  // Real unmount only — on a dependency change this would tear down a hold in
  // progress. `abandon` reads everything through refs, so it never goes stale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => abandon(), []);

  useEffect(() => {
    if (!enabled) return;

    const codes = [code];

    const beginHold = (generation: number) => {
      const s = state.current;
      s.timer = null;
      s.holdStarted = true;

      /** Apply the outcome of the start exactly once, for this press only. */
      const settle = (result: void | boolean) => {
        const cur = state.current;
        // A newer press owns the machine — this continuation is stale.
        if (cur.generation !== generation) return;
        cur.startInFlight = null;

        if (result === false) {
          // The caller already unwound whatever it opened — nothing to end.
          cur.holdStarted = false;
          cur.releaseDeferred = false;
          discardArm();
          return;
        }

        if (cur.armed) {
          cur.armed = false;
          cb.current.onArmCommitted?.();
        }

        // A release landed while the start was resolving: apply it now, once.
        if (cur.releaseDeferred) {
          cur.releaseDeferred = false;
          cur.holdStarted = false;
          cb.current.onHoldEnd();
        }
      };

      // Called synchronously: a sync start must be live immediately, or a
      // key-up in the same tick would be needlessly deferred. Only a genuine
      // promise opens an in-flight window.
      let result: void | boolean | Promise<void | boolean>;
      try {
        result = cb.current.onHoldStart();
      } catch {
        settle(false);
        return;
      }

      if (result instanceof Promise) {
        s.startInFlight = generation;
        void result.catch(() => false as const).then(settle);
        return;
      }
      settle(result);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      // A talk key activates no control, so a focused button is irrelevant —
      // unlike Space and Enter, which the browser uses to click things.
      const claimable = canClaimGlobalKey(e, {
        codes,
        respectActivatableTarget: false,
        isTyping: cb.current.isTyping,
      });
      if (!claimable) return;

      const s = state.current;
      if (s.keyOwned) return;
      // A previous press is still resolving; taking this one would let its
      // pending release end THIS press's turn.
      if (s.startInFlight !== null) return;

      s.keyOwned = true;
      s.holdStarted = false;
      s.releaseDeferred = false;
      clearTimer();
      const generation = ++s.generation;
      s.armed = cb.current.onArm ? cb.current.onArm() !== false : false;
      s.timer = setTimeout(() => beginHold(generation), thresholdMs);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== code) return;
      // Ownership decides this, not the target: if focus moved into an input
      // mid-press, re-running the typing guard would strand the press.
      const s = state.current;
      if (!s.keyOwned) return;
      s.keyOwned = false;
      clearTimer();

      if (s.startInFlight !== null) {
        s.releaseDeferred = true;
        return;
      }
      if (s.holdStarted) {
        s.holdStarted = false;
        cb.current.onHoldEnd();
        return;
      }
      // Shorter than the threshold: a tap. Nothing irreversible happened.
      discardArm();
      cb.current.onTap?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    // Includes pagehide: an iOS backgrounding or bfcache navigation mid-hold
    // would otherwise leave the microphone recording with nothing to stop it.
    const unsubscribeInterrupts = onPressInterrupt(() => abandon());
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      unsubscribeInterrupts();
      // Disabling mid-press removes the listeners that would have ended it.
      abandon();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, code, thresholdMs]);
}
